/**
 * User Role Management View
 * Component for managing user roles - displays user list and role assignment interface
 */

import React, { useState, useCallback, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  XMarkIcon,
  ExclamationTriangleIcon,
  ArrowDownTrayIcon,
} from "@heroicons/react/24/outline";
import {
  fetchUsers,
  fetchRoles,
  assignRolesToUser,
  removeRoleFromUser,
  exportUsersCsv,
} from "../../../api/rbac";
import { updateUserSegments } from "../../../api/client";
import {
  useMarkUserForDeletion,
  useCancelUserDeletion,
} from "../../../api/mutations";
import { useGetUserDeletionStatus } from "../../../api/queries";
import { getRbacErrorMessage } from "../../../constants/rbacMessages";
import { UserListItem } from "../../../types/rbac";
import { CognitoPoolEnum, UserSegmentEnum } from "../../../types";
import { getSegmentLabel } from "../../../utils/segments";
import Card from "../../../componentLibrary/Card";
import Button from "../../../componentLibrary/Button";
import Checkbox from "../../../componentLibrary/Checkbox";
import Input from "../../../componentLibrary/Input";
import Modal from "../../../componentLibrary/Modal";
import Spinner from "../../../componentLibrary/Spinner";
import MultiSelectDropdown, {
  MultiSelectOptionI,
} from "../../../componentLibrary/MultiSelectDropdown";
import Combobox, { ComboboxOptionI } from "../../../componentLibrary/Combobox";
import InfoCard from "../../../componentLibrary/InfoCard";
import { useNotification } from "../../../contexts/NotificationContext";
import { usePermissions } from "../../../contexts/PermissionContext";

const EXPORT_FIELD_LABELS: Record<string, string> = {
  email: "Email",
  username: "Username",
  roles: "Roles",
  segments: "Segments",
  lastLogin: "Last Login",
  emailAccountIssues: "Email Pref: Account Issues",
  emailFeedbackRequests: "Email Pref: Feedback Requests",
  emailNewsUpdates: "Email Pref: News & Updates",
};

const UserRoleManagementView = () => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotification();
  const { userProfile } = usePermissions();
  const currentUserSub = userProfile?.userSub;

  // State
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<ComboboxOptionI | undefined>(
    undefined,
  );
  const [segmentFilter, setSegmentFilter] = useState<
    ComboboxOptionI | undefined
  >(undefined);
  const [cognitoPoolFilter, setCognitoPoolFilter] = useState<
    ComboboxOptionI | undefined
  >(undefined);
  const [emailPreferenceFilter, setEmailPreferenceFilter] = useState<
    ComboboxOptionI | undefined
  >(undefined);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportFields, setExportFields] = useState<Record<string, boolean>>({
    email: true,
    username: true,
    roles: false,
    segments: false,
    lastLogin: false,
    emailAccountIssues: false,
    emailFeedbackRequests: false,
    emailNewsUpdates: false,
  });

  const [selectedUser, setSelectedUser] = useState<UserListItem | null>(null);
  const [selectedRoleOptions, setSelectedRoleOptions] = useState<
    MultiSelectOptionI<string>[]
  >([]);
  const [selectedSegmentOptions, setSelectedSegmentOptions] = useState<
    MultiSelectOptionI<string>[]
  >([]);

  // Fetch users
  const {
    data: usersData,
    isLoading: loadingUsers,
    error: usersError,
  } = useQuery({
    queryKey: [
      "users",
      currentPage,
      pageSize,
      search,
      roleFilter,
      segmentFilter,
      cognitoPoolFilter,
      emailPreferenceFilter,
    ],
    queryFn: () =>
      fetchUsers({
        page: currentPage,
        pageSize,
        search: search || undefined,
        role: roleFilter?.value || undefined,
        segment: segmentFilter?.value || undefined,
        cognitoPool: cognitoPoolFilter?.value || undefined,
        emailAccountIssues:
          emailPreferenceFilter?.value === "emailAccountIssues"
            ? "true"
            : undefined,
        emailFeedbackRequests:
          emailPreferenceFilter?.value === "emailFeedbackRequests"
            ? "true"
            : undefined,
        emailNewsUpdates:
          emailPreferenceFilter?.value === "emailNewsUpdates"
            ? "true"
            : undefined,
      }),
  });

  // Get current server's pool from the users API response
  const currentServerPool = usersData?.currentServerPool || null;

  // Fetch all roles for assignment dropdown
  const { data: rolesData, isLoading: loadingRoles } = useQuery({
    queryKey: ["roles"],
    queryFn: () => fetchRoles({ pageSize: 100 }), // Get all roles
  });

  // Mutation to assign roles
  const assignRolesMutation = useMutation({
    mutationFn: ({
      userSub,
      roleIds,
    }: {
      userSub: string;
      roleIds: string[];
    }) => assignRolesToUser(userSub, { roleIds }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      addNotification("success", "Roles assigned successfully");
      setSelectedRoleOptions([]);
    },
    onError: (error) => {
      addNotification("error", getRbacErrorMessage(error));
    },
  });

  // Mutation to remove role
  const removeRoleMutation = useMutation({
    mutationFn: ({ userSub, roleId }: { userSub: string; roleId: string }) =>
      removeRoleFromUser(userSub, roleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      addNotification("success", "Role removed successfully");
    },
    onError: (error) => {
      addNotification("error", getRbacErrorMessage(error));
    },
  });

  // Mutation to update segments
  const updateSegmentsMutation = useMutation({
    mutationFn: ({
      userSub,
      segments,
    }: {
      userSub: string;
      segments: string[];
    }) => updateUserSegments(userSub, segments),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      addNotification("success", "User segments updated successfully");
      setSelectedSegmentOptions([]);
    },
    onError: (error) => {
      addNotification("error", getRbacErrorMessage(error));
    },
  });

  // Query to get deletion status for selected user
  const { data: deletionStatus } = useGetUserDeletionStatus(
    selectedUser?.userSub || null,
  );

  // Mutation to mark user for deletion
  const markForDeletionMutation = useMarkUserForDeletion({
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      queryClient.invalidateQueries({
        queryKey: ["userDeletionStatus", selectedUser?.userSub],
      });
      addNotification(
        "success",
        "User marked for deletion. The account will be permanently deleted in 30 days.",
      );
    },
    onError: (error) => {
      addNotification("error", getRbacErrorMessage(error));
    },
  });

  // Mutation to cancel user deletion
  const cancelDeletionMutation = useCancelUserDeletion({
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      queryClient.invalidateQueries({
        queryKey: ["userDeletionStatus", selectedUser?.userSub],
      });
      addNotification(
        "success",
        "User deletion request cancelled successfully",
      );
    },
    onError: (error) => {
      addNotification("error", getRbacErrorMessage(error));
    },
  });

  // Handle search
  const handleSearch = useCallback((value: string) => {
    setSearch(value);
    setCurrentPage(1);
  }, []);

  // Handle assign roles
  const handleAssignRoles = useCallback(() => {
    if (!selectedUser || selectedRoleOptions.length === 0) return;

    // Check for duplicate roles
    const existingRoleIds = selectedUser.roles.map((role) => role._id);
    const roleIds = selectedRoleOptions.map((opt) => opt.value);
    const duplicateRoles = roleIds.filter((id) => existingRoleIds.includes(id));

    if (duplicateRoles.length > 0) {
      const duplicateRoleNames = selectedRoleOptions
        .filter((opt) => duplicateRoles.includes(opt.value))
        .map((opt) => opt.label)
        .join(", ");
      addNotification(
        "error",
        `User already has the following role(s): ${duplicateRoleNames}`,
      );
      return;
    }

    assignRolesMutation.mutate({
      userSub: selectedUser.userSub,
      roleIds,
    });
  }, [selectedUser, selectedRoleOptions, assignRolesMutation, addNotification]);

  // Handle remove role
  const handleRemoveRole = useCallback(
    (userSub: string, roleId: string) => {
      // Prevent users from removing their own roles
      if (currentUserSub === userSub) {
        addNotification(
          "error",
          "You cannot remove roles from yourself. Please ask another administrator to modify your roles.",
        );
        return;
      }

      if (
        window.confirm(
          "Are you sure you want to remove this role from the user?",
        )
      ) {
        removeRoleMutation.mutate({ userSub, roleId });
      }
    },
    [removeRoleMutation, currentUserSub, addNotification],
  );

  // Handle mark user for deletion
  const handleMarkForDeletion = useCallback(() => {
    if (!selectedUser) return;

    // Prevent users from marking themselves for deletion
    if (currentUserSub === selectedUser.userSub) {
      addNotification(
        "error",
        "You cannot mark yourself for deletion. Please ask another administrator to perform this action.",
      );
      return;
    }

    const confirmMessage = `Are you sure you want to mark this user for deletion?\n\nUser: ${selectedUser.username || selectedUser.email || selectedUser.userSub}\n\nThis will mark the account for deletion. The account will be permanently deleted in 30 days. This action cannot be easily undone.`;

    if (window.confirm(confirmMessage)) {
      markForDeletionMutation.mutate(selectedUser.userSub);
    }
  }, [selectedUser, currentUserSub, markForDeletionMutation, addNotification]);

  // Handle cancel user deletion
  const handleCancelDeletion = useCallback(() => {
    if (!selectedUser) return;

    const confirmMessage = `Are you sure you want to cancel the deletion request for this user?\n\nUser: ${selectedUser.username || selectedUser.email || selectedUser.userSub}\n\nThis will cancel the account deletion request and restore the account.`;

    if (window.confirm(confirmMessage)) {
      cancelDeletionMutation.mutate(selectedUser.userSub);
    }
  }, [selectedUser, cancelDeletionMutation]);

  // Handle update segments
  const handleUpdateSegments = useCallback(() => {
    if (!selectedUser) return;

    const segments = selectedSegmentOptions.map((opt) => opt.value);

    updateSegmentsMutation.mutate({
      userSub: selectedUser.userSub,
      segments,
    });
  }, [selectedUser, selectedSegmentOptions, updateSegmentsMutation]);

  // Update selected user when users data changes
  useEffect(() => {
    if (selectedUser && usersData) {
      const updatedUser = usersData.items.find(
        (u) => u.userSub === selectedUser.userSub,
      );
      if (updatedUser) {
        setSelectedUser(updatedUser);
      }
    }
  }, [usersData, selectedUser]);

  // Initialize selected segments when user is selected
  useEffect(() => {
    if (selectedUser && selectedUser.segments) {
      setSelectedSegmentOptions(
        selectedUser.segments.map((segment) => ({
          value: segment,
          label: getSegmentLabel(segment as UserSegmentEnum),
        })),
      );
    } else {
      setSelectedSegmentOptions([]);
    }
  }, [selectedUser]);

  const users = usersData?.items || [];
  const totalPages = usersData
    ? Math.ceil(usersData.total / usersData.pageSize)
    : 0;
  const roles = rolesData?.items || [];

  const handleExport = useCallback(async () => {
    const selectedFields = Object.entries(exportFields)
      .filter(([, enabled]) => enabled)
      .map(([field]) => field);

    if (selectedFields.length === 0) {
      addNotification("error", "Please select at least one field to export.");
      return;
    }

    setIsExporting(true);
    try {
      const blob = await exportUsersCsv({
        search: search || undefined,
        role: roleFilter?.value || undefined,
        segment: segmentFilter?.value || undefined,
        cognitoPool: cognitoPoolFilter?.value || undefined,
        emailAccountIssues:
          emailPreferenceFilter?.value === "emailAccountIssues"
            ? "true"
            : undefined,
        emailFeedbackRequests:
          emailPreferenceFilter?.value === "emailFeedbackRequests"
            ? "true"
            : undefined,
        emailNewsUpdates:
          emailPreferenceFilter?.value === "emailNewsUpdates"
            ? "true"
            : undefined,
        fields: selectedFields,
      });

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `users-export-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);

      setIsExportModalOpen(false);
      addNotification("success", "Users exported successfully.");
    } catch {
      addNotification("error", "Failed to export users.");
    } finally {
      setIsExporting(false);
    }
  }, [
    exportFields,
    search,
    roleFilter,
    segmentFilter,
    cognitoPoolFilter,
    emailPreferenceFilter,
    addNotification,
  ]);

  return (
    <>
      <p className="text-gray-600 mb-6">
        Manage user roles and permissions. Assign or remove roles from users.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* User List */}
        <div className="lg:col-span-5">
          <Card
            title={
              <div className="flex flex-row items-center justify-between w-full gap-4 pr-2">
                <span>Users</span>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setIsExportModalOpen(true)}
                  className="flex items-center gap-1.5"
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  Export CSV
                </Button>
              </div>
            }
          >
            {/* Search and Filters */}
            <div className="mb-4 space-y-3">
              <Input
                type="text"
                placeholder="Search by username, email, or userSub..."
                value={search}
                onChange={(e) => handleSearch(e.target.value)}
              />

              {/* Role Filter */}
              <div>
                <div className="flex gap-2 items-end">
                  <div className="flex-1">
                    <Combobox
                      key={
                        roleFilter ? `role-${roleFilter.value}` : "role-empty"
                      }
                      label="Filter by Role"
                      options={[
                        { value: "", label: "All Users" },
                        ...(rolesData?.items.map((role) => ({
                          value: role.name,
                          label: `${role.displayName} (${role.name})`,
                        })) || []),
                      ]}
                      value={roleFilter}
                      onChange={(option) => {
                        setRoleFilter(option.value === "" ? undefined : option);
                        setCurrentPage(1);
                      }}
                      placeholder="All Users"
                    />
                  </div>
                  {roleFilter && (
                    <Button
                      onClick={() => setRoleFilter(undefined)}
                      variant="secondary"
                      size="sm"
                      className="flex-shrink-0 h-[42px]"
                      title="Clear role filter"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>

              {/* Segment Filter */}
              <div>
                <div className="flex gap-2 items-end">
                  <div className="flex-1">
                    <Combobox
                      key={
                        segmentFilter
                          ? `segment-${segmentFilter.value}`
                          : "segment-empty"
                      }
                      label="Filter by Segment"
                      options={[
                        { value: "", label: "All Users" },
                        ...Object.values(UserSegmentEnum).map((segment) => ({
                          value: segment,
                          label: getSegmentLabel(segment as UserSegmentEnum),
                        })),
                      ]}
                      value={segmentFilter}
                      onChange={(option) => {
                        setSegmentFilter(
                          option.value === "" ? undefined : option,
                        );
                        setCurrentPage(1);
                      }}
                      placeholder="All Users"
                    />
                  </div>
                  {segmentFilter && (
                    <Button
                      onClick={() => setSegmentFilter(undefined)}
                      variant="secondary"
                      size="sm"
                      className="flex-shrink-0 h-[42px]"
                      title="Clear segment filter"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>

              {/* Cognito Pool Filter */}
              <div>
                <div className="flex gap-2 items-end">
                  <div className="flex-1">
                    <Combobox
                      key={
                        cognitoPoolFilter
                          ? `cognitoPool-${cognitoPoolFilter.value}`
                          : "cognitoPool-empty"
                      }
                      label="Filter by Cognito Pool"
                      options={[
                        { value: "", label: "All Users" },
                        { value: "unknown", label: "Unknown" },
                        ...(currentServerPool
                          ? [
                              {
                                value: currentServerPool,
                                label:
                                  currentServerPool === CognitoPoolEnum.STAGING
                                    ? "Staging"
                                    : "Production",
                              },
                            ]
                          : []),
                      ]}
                      value={cognitoPoolFilter}
                      onChange={(option) => {
                        setCognitoPoolFilter(
                          option.value === "" ? undefined : option,
                        );
                        setCurrentPage(1);
                      }}
                      placeholder="All Users"
                    />
                  </div>
                  {cognitoPoolFilter && (
                    <Button
                      onClick={() => setCognitoPoolFilter(undefined)}
                      variant="secondary"
                      size="sm"
                      className="flex-shrink-0 h-[42px]"
                      title="Clear Cognito pool filter"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>

              {/* Email Preference Filter */}
              <div>
                <div className="flex gap-2 items-end">
                  <div className="flex-1">
                    <Combobox
                      key={
                        emailPreferenceFilter
                          ? `email-${emailPreferenceFilter.value}`
                          : "email-empty"
                      }
                      label="Filter by Email Preference"
                      options={[
                        { value: "", label: "All Users" },
                        {
                          value: "emailAccountIssues",
                          label:
                            "Issues with my account (e.g. security issues)",
                        },
                        {
                          value: "emailFeedbackRequests",
                          label:
                            "Requests for feedback to improve my experience with the app",
                        },
                        {
                          value: "emailNewsUpdates",
                          label: "News and updates about the app",
                        },
                      ]}
                      value={emailPreferenceFilter}
                      onChange={(option) => {
                        setEmailPreferenceFilter(
                          option.value === "" ? undefined : option,
                        );
                        setCurrentPage(1);
                      }}
                      placeholder="All Users"
                    />
                  </div>
                  {emailPreferenceFilter && (
                    <Button
                      onClick={() => setEmailPreferenceFilter(undefined)}
                      variant="secondary"
                      size="sm"
                      className="flex-shrink-0 h-[42px]"
                      title="Clear email preference filter"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Loading State */}
            {loadingUsers && (
              <div className="flex justify-center py-8">
                <Spinner />
              </div>
            )}

            {/* Error State */}
            {usersError && (
              <div className="text-red-600 py-4 p-4 bg-red-50 rounded border border-red-200">
                <div className="font-semibold mb-2">Error loading users</div>
                <div className="text-sm">{(usersError as Error).message}</div>
                {(roleFilter ||
                  segmentFilter ||
                  cognitoPoolFilter ||
                  emailPreferenceFilter) && (
                  <div className="mt-3 text-sm">
                    <button
                      onClick={() => {
                        setRoleFilter(undefined);
                        setSegmentFilter(undefined);
                        setCognitoPoolFilter(undefined);
                        setEmailPreferenceFilter(undefined);
                      }}
                      className="text-electrum-600 hover:text-electrum-700 underline"
                    >
                      Clear filters and try again
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* User List */}
            {!loadingUsers && !usersError && (
              <>
                {/* Results Info */}
                <div className="mb-3 text-sm text-gray-600">
                  {roleFilter ||
                  segmentFilter ||
                  cognitoPoolFilter ||
                  emailPreferenceFilter ? (
                    <div>
                      <span>
                        Showing {users.length} user(s)
                        {roleFilter && (
                          <>
                            {" "}
                            with role: <strong>{roleFilter.label}</strong>
                          </>
                        )}
                        {roleFilter && segmentFilter && " and"}
                        {segmentFilter && (
                          <>
                            {" "}
                            with segment: <strong>{segmentFilter.label}</strong>
                          </>
                        )}
                        {(roleFilter || segmentFilter) &&
                          cognitoPoolFilter &&
                          " and"}
                        {cognitoPoolFilter && (
                          <>
                            {" "}
                            with Cognito pool:{" "}
                            <strong>{cognitoPoolFilter.label}</strong>
                          </>
                        )}
                        {(roleFilter || segmentFilter || cognitoPoolFilter) &&
                          emailPreferenceFilter &&
                          " and"}
                        {emailPreferenceFilter && (
                          <>
                            {" "}
                            opted in to:{" "}
                            <strong>{emailPreferenceFilter.label}</strong>
                          </>
                        )}
                      </span>
                      {users.length === 0 && (
                        <div className="mt-2">
                          <InfoCard variant="warning" title="No users found">
                            <div>
                              No users found matching the selected filters. This
                              could mean:
                              <ul className="list-disc list-inside mt-1">
                                {roleFilter && (
                                  <li>
                                    No users have been assigned this role yet
                                  </li>
                                )}
                                {segmentFilter && (
                                  <li>
                                    No users have been assigned this segment yet
                                  </li>
                                )}
                                {cognitoPoolFilter && (
                                  <li>
                                    No users match this Cognito pool filter
                                  </li>
                                )}
                                {emailPreferenceFilter && (
                                  <li>
                                    No users have opted in to this email
                                    preference
                                  </li>
                                )}
                                <li>
                                  The filter criteria may not exist in the
                                  system
                                </li>
                              </ul>
                              Try clearing the filter(s) to see all users.
                            </div>
                          </InfoCard>
                        </div>
                      )}
                    </div>
                  ) : (
                    <span>Showing {users.length} user(s)</span>
                  )}
                  {usersData && usersData.total > users.length && (
                    <span> (Total: {usersData.total})</span>
                  )}
                </div>

                <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                  {users.map((user) => (
                    <div
                      key={user._id}
                      className={`p-3 border rounded-lg cursor-pointer hover:border-electrum-300 transition-colors ${
                        user.isDeleted
                          ? "border-red-400 bg-red-100"
                          : user.hasInvalidRoles
                            ? "border-red-300 bg-red-50"
                            : selectedUser?.userSub === user.userSub
                              ? "border-electrum-500 bg-electrum-50"
                              : "border-gray-200"
                      }`}
                      onClick={() => setSelectedUser(user)}
                    >
                      <div className="flex items-center gap-2">
                        <div
                          className={`font-semibold text-sm ${
                            user.isDeleted
                              ? "text-red-800"
                              : user.hasInvalidRoles
                                ? "text-red-700"
                                : ""
                          }`}
                        >
                          {user.username || user.email || "No name"}
                        </div>
                        {user.cognitoPool && (
                          <span
                            className={`text-xs px-2 py-0.5 border rounded font-medium
                              ${
                                user.cognitoPool === "unknown"
                                  ? "bg-yellow-100 text-yellow-800 border-yellow-300"
                                  : "bg-green-100 text-green-800 border-green-300"
                              }`}
                          >
                            {user.cognitoPool === "unknown"
                              ? "Unknown Pool"
                              : user.cognitoPool.charAt(0).toUpperCase() +
                                user.cognitoPool.slice(1).toLowerCase()}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500">{user.email}</div>
                      <div className="text-xs text-gray-400 mt-1">
                        {user.roles.length} role(s)
                      </div>
                      {user.lastLogin && (
                        <div className="text-xs text-gray-500 mt-1">
                          Last login:{" "}
                          {new Date(user.lastLogin).toLocaleDateString()} at{" "}
                          {new Date(user.lastLogin).toLocaleTimeString()}
                        </div>
                      )}
                      {user.isDeleted && (
                        <div className="text-xs text-red-700 mt-1 font-medium">
                          ⚠️ Marked for deletion
                        </div>
                      )}
                      {user.hasInvalidRoles && (
                        <div className="flex items-center gap-1 text-xs text-red-600 mt-1 font-medium">
                          <ExclamationTriangleIcon className="h-3 w-3" />
                          Invalid role format detected
                        </div>
                      )}
                    </div>
                  ))}

                  {users.length === 0 &&
                    !roleFilter &&
                    !segmentFilter &&
                    !cognitoPoolFilter &&
                    !emailPreferenceFilter && (
                      <InfoCard variant="info" title="No users found">
                        No users found in the system.
                      </InfoCard>
                    )}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-between items-center mt-4 pt-4 border-t">
                    <Button
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      variant="secondary"
                      size="sm"
                    >
                      Previous
                    </Button>
                    <span className="text-sm text-gray-600">
                      Page {currentPage} of {totalPages}
                    </span>
                    <Button
                      disabled={currentPage === totalPages}
                      onClick={() =>
                        setCurrentPage((p) => Math.min(totalPages, p + 1))
                      }
                      variant="secondary"
                      size="sm"
                    >
                      Next
                    </Button>
                  </div>
                )}
              </>
            )}
          </Card>
        </div>

        {/* User Details & Role Assignment */}
        <div className="lg:col-span-7">
          {selectedUser ? (
            <div className="space-y-4">
              {/* User Info Card */}
              <div
                className={
                  deletionStatus?.isDeleted
                    ? "bg-red-50 border-2 border-red-200 rounded-md"
                    : ""
                }
              >
                <Card title="User Details">
                  <div className="space-y-2">
                    <div>
                      <span className="font-semibold">Username:</span>{" "}
                      {selectedUser.username || "N/A"}
                    </div>
                    <div>
                      <span className="font-semibold">Email:</span>{" "}
                      {selectedUser.email || "N/A"}
                    </div>
                    <div>
                      <span className="font-semibold">Segments:</span>{" "}
                      {selectedUser.segments &&
                      selectedUser.segments.length > 0 ? (
                        <span className="inline-flex flex-wrap gap-1 ml-1">
                          {selectedUser.segments.map((segment) => (
                            <span
                              key={segment}
                              className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-800 border border-blue-300"
                            >
                              {getSegmentLabel(segment as UserSegmentEnum)}
                            </span>
                          ))}
                        </span>
                      ) : (
                        "None"
                      )}
                    </div>
                    <div>
                      <span className="font-semibold">Last Login:</span>{" "}
                      {selectedUser.lastLogin ? (
                        <>
                          {new Date(
                            selectedUser.lastLogin,
                          ).toLocaleDateString()}{" "}
                          at{" "}
                          {new Date(
                            selectedUser.lastLogin,
                          ).toLocaleTimeString()}
                        </>
                      ) : (
                        "Never"
                      )}
                    </div>
                    <div>
                      <span className="font-semibold">Email Preferences:</span>
                      <span className="inline-flex flex-wrap gap-1 ml-2">
                        {(
                          [
                            {
                              key: "emailAccountIssues" as const,
                              label: "Issues",
                            },
                            {
                              key: "emailFeedbackRequests" as const,
                              label: "Feedback",
                            },
                            {
                              key: "emailNewsUpdates" as const,
                              label: "News",
                            },
                          ] as const
                        ).map(({ key, label }) => {
                          const value =
                            selectedUser.emailPreferences?.[key] ?? null;
                          if (value !== true) return null;
                          return (
                            <span
                              key={key}
                              className="text-xs font-medium px-1.5 py-0.5 rounded bg-green-100 text-green-700 border border-green-300"
                            >
                              {label}
                            </span>
                          );
                        })}
                        {!selectedUser.emailPreferences?.emailAccountIssues &&
                          !selectedUser.emailPreferences
                            ?.emailFeedbackRequests &&
                          !selectedUser.emailPreferences?.emailNewsUpdates && (
                            <span className="text-sm text-gray-500">None</span>
                          )}
                      </span>
                    </div>
                    {selectedUser.hasInvalidRoles && (
                      <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded">
                        <div className="flex items-center gap-1 text-red-700 font-semibold text-sm mb-1">
                          <ExclamationTriangleIcon className="h-4 w-4" />
                          Invalid Role Data Detected
                        </div>
                        <div className="text-red-600 text-xs">
                          This user has invalid role IDs in their database
                          record. These invalid entries have been filtered out
                          from the display below.
                        </div>
                        {selectedUser.invalidRoleIds &&
                          selectedUser.invalidRoleIds.length > 0 && (
                            <div className="mt-2 text-xs text-red-500">
                              Invalid IDs:{" "}
                              {selectedUser.invalidRoleIds.join(", ")}
                            </div>
                          )}
                      </div>
                    )}
                  </div>
                </Card>
              </div>

              {/* Current Roles Card */}
              <div
                className={
                  deletionStatus?.isDeleted
                    ? "bg-red-50 border-2 border-red-200 rounded-md"
                    : ""
                }
              >
                <Card title={`Current Roles (${selectedUser.roles.length})`}>
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {selectedUser.roles.length > 0 ? (
                      selectedUser.roles.map((role) => (
                        <div
                          key={role._id}
                          className="flex justify-between items-center p-3 border rounded-lg"
                        >
                          <div>
                            <div className="font-semibold">
                              {role.displayName}
                            </div>
                            <div className="text-sm text-gray-500">
                              {role.name}
                            </div>
                            <div className="text-xs text-gray-400 mt-1">
                              {role.permissions?.length || 0} permission(s)
                            </div>
                          </div>
                          <Button
                            variant="cautionary"
                            size="sm"
                            onClick={() =>
                              handleRemoveRole(selectedUser.userSub, role._id)
                            }
                            disabled={
                              removeRoleMutation.isPending ||
                              selectedUser.userSub === currentUserSub
                            }
                            title={
                              selectedUser.userSub === currentUserSub
                                ? "You cannot remove roles from yourself"
                                : "Remove role"
                            }
                          >
                            <XMarkIcon className="h-4 w-4" />
                          </Button>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-6 text-gray-500">
                        No roles assigned
                      </div>
                    )}
                  </div>
                </Card>
              </div>

              {/* Assign Roles Card */}
              <div
                className={
                  deletionStatus?.isDeleted
                    ? "bg-red-50 border-2 border-red-200 rounded-md"
                    : ""
                }
              >
                <Card title="Assign New Roles">
                  {loadingRoles ? (
                    <div className="flex justify-center py-4">
                      <Spinner />
                    </div>
                  ) : (
                    <div className="space-y-4 pb-4">
                      <MultiSelectDropdown
                        label="Select Roles to Assign"
                        options={roles
                          .filter(
                            (role) =>
                              !selectedUser?.roles.some(
                                (r) => r._id === role._id,
                              ),
                          )
                          .map((role) => ({
                            value: role._id,
                            label: role.displayName,
                          }))}
                        value={selectedRoleOptions}
                        onChange={setSelectedRoleOptions}
                        placeholder="Choose roles..."
                      />
                      <Button
                        onClick={handleAssignRoles}
                        disabled={
                          selectedRoleOptions.length === 0 ||
                          assignRolesMutation.isPending
                        }
                        className="w-full"
                      >
                        {assignRolesMutation.isPending
                          ? "Assigning..."
                          : "Assign Roles"}
                      </Button>
                    </div>
                  )}
                </Card>
              </div>

              {/* User Segments Card */}
              <div
                className={
                  deletionStatus?.isDeleted
                    ? "bg-red-50 border-2 border-red-200 rounded-md"
                    : ""
                }
              >
                <Card title="User Segments">
                  <div className="space-y-4 pb-4">
                    <div>
                      <div className="text-sm text-gray-600 mb-2">
                        Current Segments ({selectedUser.segments?.length || 0})
                      </div>
                      {selectedUser.segments &&
                      selectedUser.segments.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {selectedUser.segments.map((segment) => (
                            <span
                              key={segment}
                              className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800 border border-blue-300"
                            >
                              {getSegmentLabel(segment as UserSegmentEnum)}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-4 text-gray-500 text-sm">
                          No segments assigned
                        </div>
                      )}
                    </div>
                    <MultiSelectDropdown
                      label="Update Segments"
                      options={Object.values(UserSegmentEnum).map(
                        (segment) => ({
                          value: segment,
                          label: getSegmentLabel(segment as UserSegmentEnum),
                        }),
                      )}
                      value={selectedSegmentOptions}
                      onChange={setSelectedSegmentOptions}
                      placeholder="Choose segments..."
                    />
                    <Button
                      onClick={handleUpdateSegments}
                      disabled={updateSegmentsMutation.isPending}
                      className="w-full"
                    >
                      {updateSegmentsMutation.isPending
                        ? "Updating..."
                        : "Update Segments"}
                    </Button>
                  </div>
                </Card>
              </div>

              {/* Account Deletion Card */}
              <div
                className={
                  deletionStatus?.isDeleted
                    ? "bg-red-50 border-2 border-red-200 rounded-md"
                    : ""
                }
              >
                <Card title="Account Changes">
                  <div className="space-y-4 pb-4">
                    {deletionStatus?.isDeleted ? (
                      <div className="p-4 bg-red-100 border border-red-300 rounded-lg">
                        <div className="text-red-900 font-semibold mb-2">
                          ⚠️ Account Marked for Deletion
                        </div>
                        <div className="text-sm text-red-800 mb-2">
                          This account has been marked for deletion. The account
                          will be permanently deleted in 30 days from the
                          deletion request date.
                        </div>
                        {deletionStatus.daysRemaining !== undefined && (
                          <div className="text-sm text-red-700 mb-4 font-medium">
                            {deletionStatus.daysRemaining > 0 ? (
                              <>
                                <strong>{deletionStatus.daysRemaining}</strong>{" "}
                                day
                                {deletionStatus.daysRemaining !== 1
                                  ? "s"
                                  : ""}{" "}
                                remaining before permanent deletion
                              </>
                            ) : (
                              <strong>
                                Account scheduled for immediate deletion
                              </strong>
                            )}
                          </div>
                        )}
                        {deletionStatus.permanentDeletionDate && (
                          <div className="text-xs text-red-600 mb-4">
                            Permanent deletion date:{" "}
                            {new Date(
                              deletionStatus.permanentDeletionDate,
                            ).toLocaleString()}
                          </div>
                        )}
                        <Button
                          onClick={handleCancelDeletion}
                          disabled={
                            cancelDeletionMutation.isPending ||
                            deletionStatus.daysRemaining === 0
                          }
                          variant="secondary"
                          className="w-full"
                          title={
                            deletionStatus.daysRemaining === 0
                              ? "Cannot cancel deletion after grace period expires"
                              : "Cancel deletion request"
                          }
                        >
                          {cancelDeletionMutation.isPending
                            ? "Cancelling..."
                            : "Cancel Deletion Request"}
                        </Button>
                      </div>
                    ) : (
                      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                        <div className="text-red-800 font-semibold mb-2">
                          Mark User for Deletion
                        </div>
                        <div className="text-sm text-red-700 mb-4">
                          This will mark the user account for deletion. The
                          account will be permanently deleted in 30 days. This
                          action can be cancelled within 30 days.
                        </div>
                        <Button
                          onClick={handleMarkForDeletion}
                          disabled={
                            markForDeletionMutation.isPending ||
                            selectedUser.userSub === currentUserSub
                          }
                          variant="cautionary"
                          className="w-full"
                          title={
                            selectedUser.userSub === currentUserSub
                              ? "You cannot mark yourself for deletion"
                              : "Mark user for deletion"
                          }
                        >
                          {markForDeletionMutation.isPending
                            ? "Marking for deletion..."
                            : "Mark User for Deletion"}
                        </Button>
                      </div>
                    )}
                  </div>
                </Card>
              </div>
            </div>
          ) : (
            <Card>
              <div className="text-center py-12 text-gray-500">
                Select a user from the list to view details and manage roles
              </div>
            </Card>
          )}
        </div>
      </div>

      {/* Export CSV Modal */}
      <Modal
        isOpen={isExportModalOpen}
        title="Export Users as CSV"
        subtitle="Select the fields you want to include in the export. Only users matching the current filters will be exported."
        size="md"
        height="auto"
        maxHeight="90vh"
        onCloseModal={() => setIsExportModalOpen(false)}
        footer={
          <div className="flex gap-2 justify-end">
            <Button
              variant="secondary"
              onClick={() => setIsExportModalOpen(false)}
              disabled={isExporting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleExport}
              disabled={
                isExporting || !Object.values(exportFields).some(Boolean)
              }
              loading={isExporting}
              className="flex items-center gap-1.5"
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              {isExporting ? "Exporting..." : "Export"}
            </Button>
          </div>
        }
      >
        <div className="py-2 space-y-1">
          {Object.entries(EXPORT_FIELD_LABELS).map(([field, label]) => (
            <div
              key={field}
              className="flex items-center p-2 rounded-lg hover:bg-gray-50"
            >
              <Checkbox
                label={label}
                checked={exportFields[field]}
                onChange={(e) =>
                  setExportFields((prev) => ({
                    ...prev,
                    [field]: e.target.checked,
                  }))
                }
              />
            </div>
          ))}
          {!Object.values(exportFields).some(Boolean) && (
            <p className="text-xs text-red-500 mt-2">
              Select at least one field to export.
            </p>
          )}
        </div>
      </Modal>
    </>
  );
};

export default UserRoleManagementView;
